import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Modal from "@mui/material/Modal";
import { FaBars, FaTimes } from "react-icons/fa";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from "react-icons/md";
import logo from "../../../assests/Daalchini-logo.svg";
import Avtar from "../../../assests/Avatar.png";
import avtar2 from "../../../assests/emotions2.svg";
import { SidebarData } from "./SidebarData";
import "./style.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);

  // Get user data and permissions
  useEffect(() => {
    try {
      const data = localStorage.getItem("data");
      if (data) {
        const parsed = JSON.parse(data);
        setUserData(parsed);
      }
      const perms = localStorage.getItem("userPermissions");
      if (perms) {
        setPermissions(JSON.parse(perms));
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  }, []);

  // Handle responsive - using matchMedia for better accuracy
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      if (!isMobileView) {
        setIsOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto close mobile menu on route change
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Filter sidebar data based on permissions
  const filterSidebarData = (data, userPermissions) => {
    return data
      .map((item) => {
        if (item.subNav && item.subNav.length > 0) {
          const filteredSubNav = item.subNav.filter((sub) => {
            if (!sub.permissions) return true;
            return sub.permissions.some((p) => userPermissions.includes(p));
          });

          const hasPermission = !item.permissions || 
            item.permissions.some((p) => userPermissions.includes(p));

          if (hasPermission && filteredSubNav.length > 0) {
            return { ...item, subNav: filteredSubNav };
          }
          return null;
        }

        if (!item.permissions || item.permissions.some((p) => userPermissions.includes(p))) {
          return item;
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredSidebarData = filterSidebarData(SidebarData, permissions);

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Toggle dropdown
  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
    if (openDropdown === index) {
      setSelectedSubMenu(null);
    }
  };

  // Handle submenu selection
  const handleSubMenuSelect = (subItem, parentIndex) => {
    setSelectedSubMenu(subItem.title);
    navigate(subItem.path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("data");
    localStorage.removeItem("loginTime");
    localStorage.removeItem("allPermissions");
    localStorage.removeItem("userPermissions");
    window.location.href = "/";
  };

  // Check if link is active
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get user name and role
  const userName = userData?.details?.userDetails?.full_name || "User";
  const userRole = userData?.details?.userRole?.role_name || "Role";
  const userId = userData?.details?.userDetails?.id || null;

  return (
    <>
      {/* Mobile Hamburger Button */}
      {isMobile && (
        <button 
          className="ds-hamburger-btn" 
          onClick={toggleMenu} 
          aria-label={isOpen ? "Close Menu" : "Open Menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      )}

      {/* Sidebar */}
      <div 
        className={`ds-sidebar-wrapper ${isMobile ? 'ds-mobile' : ''} ${isOpen ? 'ds-open' : ''}`}
        role="navigation"
        aria-hidden={isMobile && !isOpen}
      >
        <div className="ds-sidebar-inner">
          {/* Logo */}
          <div className="ds-sidebar-logo">
            <img src={logo} alt="Daalchini" />
          </div>

          {/* Profile Section */}
          <div className="ds-sidebar-profile">
            <div className="ds-profile-row">
              <img src={Avtar} alt="User Avatar" className="ds-profile-avatar" />
              <div className="ds-profile-info">
                <p className="ds-profile-name">{userName}</p>
                <span className="ds-profile-role">{userRole}</span>
              </div>
            </div>
            
            {permissions.includes("user_dash_view") && userId && (
              <Link 
                to={`/users/userlist/${userId}`} 
                className="ds-profile-view-btn"
                onClick={() => isMobile && setIsOpen(false)}
              >
                View Profile
              </Link>
            )}
          </div>

          {/* Menu Items */}
          <div className="ds-sidebar-menu">
            {filteredSidebarData.map((item) => (
              <div key={item.index} className="ds-menu-item">
                {/* Main Menu Item */}
                {item.subNav ? (
                  <>
                    <div 
                      className={`ds-menu-title ${openDropdown === item.index ? 'ds-active' : ''}`}
                      onClick={() => toggleDropdown(item.index)}
                      role="button"
                      aria-expanded={openDropdown === item.index}
                    >
                      <div className="ds-menu-title-left">
                        <img src={item.icon} alt={item.title} className="ds-menu-icon" />
                        <span>{item.title}</span>
                      </div>
                      <span className="ds-menu-arrow">
                        {openDropdown === item.index ? 
                          <MdOutlineKeyboardArrowUp /> : 
                          <MdOutlineKeyboardArrowDown />
                        }
                      </span>
                    </div>

                    {/* Sub Menu Items - Vertical with Radio Buttons */}
                    {item.subNav && openDropdown === item.index && (
                      <div className="ds-submenu-container">
                        {item.subNav.map((sub, idx) => {
                          const isSubActive = selectedSubMenu === sub.title || isActive(sub.path);
                          return (
                            <div 
                              key={idx} 
                              className={`ds-submenu-item ${isSubActive ? 'ds-active' : ''}`}
                              onClick={() => handleSubMenuSelect(sub, item.index)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSubMenuSelect(sub, item.index);
                                }
                              }}
                            >
                              <div className="ds-submenu-radio-wrapper">
                                <input
                                  type="radio"
                                  name={`ds-submenu-${item.index}`}
                                  id={`ds-sub-${item.index}-${idx}`}
                                  checked={isSubActive}
                                  onChange={() => handleSubMenuSelect(sub, item.index)}
                                  className="ds-submenu-radio-input"
                                />
                                <label 
                                  htmlFor={`ds-sub-${item.index}-${idx}`}
                                  className="ds-submenu-radio-label"
                                >
                                  <span className="ds-submenu-radio-custom"></span>
                                  {sub.title}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Link 
                    to={item.path} 
                    className={`ds-menu-link ${isActive(item.path) ? 'ds-active' : ''}`}
                    onClick={() => isMobile && setIsOpen(false)}
                  >
                    <img src={item.icon} alt={item.title} className="ds-menu-icon" />
                    <span>{item.title}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Logout Button */}
          <button 
            className="ds-sidebar-logout-btn"
            onClick={() => setShowLogoutModal(true)}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div 
          className="ds-sidebar-overlay" 
          onClick={toggleMenu}
          role="button"
          aria-label="Close menu"
        />
      )}

      {/* Logout Modal - Properly Centered */}
      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        className="ds-logout-modal"
       
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="ds-logout-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="ds-logout-modal-header">
            <img src={avtar2} alt="Logout" className="ds-logout-modal-icon" />
          </div>
          <div className="ds-logout-modal-body">
            <h3 className="ds-logout-modal-title">Log Out</h3>
            <p className="ds-logout-modal-message">Are you sure you want to log out?</p>
          </div>
          <div className="ds-logout-modal-footer">
            <button 
              className="ds-logout-cancel-btn"
              onClick={() => setShowLogoutModal(false)}
            >
              Cancel
            </button>
            <button 
              className="ds-logout-confirm-btn"
              onClick={handleLogout}
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Sidebar;